import {ActivatedRoute, Params} from '@angular/router';
import {Component, OnInit} from '@angular/core';
import {ErrorModel} from '../../../../model/error.model';
import {MatOption} from '@angular/material/autocomplete';
import {MatFormField, MatLabel, MatSelect, MatSelectChange} from '@angular/material/select';
import {PageComponent} from '../../page.component';
import {
  MatCard,
  MatCardAvatar,
  MatCardContent,
  MatCardHeader,
  MatCardSubtitle,
  MatCardTitle
} from '@angular/material/card';
import {MatIcon} from '@angular/material/icon';
import {HttpStatusCode} from '@angular/common/http';
import {EmployeeService} from '../../../../service/employee.service';
import {DatePipe, NgIf} from '@angular/common';
import {MatTableModule} from '@angular/material/table';
import {FormsModule} from '@angular/forms';
import {ByteSizePipe} from '../../../../pipe/byte-size.pipe';
import {MatAnchor} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DocumentDialogComponent} from '../../../dialog/document/document.component';
import {EmployeeEvent} from '../../../../event/employee.event';
import {EmployeeEventTypeEnum} from '../../../../enum/employee-event-type.enum';
import {EmployeeApiDocumentsModel} from '../../../../model/employee-api-documents-model';
import {EmployeeApiDocumentModel} from '../../../../model/employee-api-document.model';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

// noinspection JSValidateJSDoc

/**
 * @class
 * @component
 * @description This class maintains the SSO page component of our application.
 * @export
 * @implements {OnInit}
 * @name SsoEmployeePageComponent
 */
@Component({
  imports: [
    ByteSizePipe,
    DatePipe,
    FormsModule,
    MatAnchor,
    MatCard,
    MatCardAvatar,
    MatCardContent,
    MatCardHeader,
    MatCardSubtitle,
    MatCardTitle,
    MatFormField,
    MatIcon,
    MatLabel,
    MatOption,
    MatSelect,
    MatTableModule,
    MatTooltip,
    NgIf,
    PageComponent,
    MatProgressSpinner
  ],
  selector: 'app-page-employee-sso',
  standalone: true,
  styleUrl: './sso.component.scss',
  templateUrl: './sso.component.html'
})
export class SsoEmployeePageComponent implements OnInit {

  /**
   * @default new Date().getFullYear()
   * @description This property contains the current year for the component.
   * @memberof SsoEmployeePageComponent
   * @name SsoEmployeePageComponent#currentYear
   * @property
   * @protected
   * @type {number}
   */
  protected currentYear: number = 0;

  /**
   * @default new EmployeeApiDocumentsModel
   * @description This property contains the documents for the employee.
   * @memberof SsoEmployeePageComponent
   * @name SsoEmployeePageComponent#documents
   * @property
   * @protected
   * @type {EmployeeApiDocumentModel[]}
   */
  protected documents: EmployeeApiDocumentModel[] = [];

  /**
   * @default true
   * @description This property contains the flag to hide the documents tables for the component.
   * @memberof SsoEmployeePageComponent
   * @name SsoEmployeePageComponent#hideDocuments
   * @property
   * @protected
   * @type {boolean}
   */
  protected hideDocuments: boolean = true;

  /**
   * @default true
   * @description This property contains the flag to hide the year select for documents.
   * @memberof SsoEmployeePageComponent
   * @name SsoEmployeePageComponent#hideYearSelect
   * @property
   * @protected
   * @type {boolean}
   */
  protected hideYearSelect: boolean = true;

  /**
   * @default false
   * @description This property contains the loading flag for the component.
   * @memberof SsoEmployeePageComponent
   * @name SsoEmployeePageComponent#loading
   * @property
   * @protected
   * @type {boolean}
   */
  protected loading: boolean = false;

  /**
   * @description This property contains the selected year for the documents.
   * @memberof SsoEmployeePageComponent
   * @name SsoEmployeePageComponent#selectedYear
   * @property
   * @protected
   * @type {number}
   */
  protected selectedYear: number = this.currentYear;

  /**
   * @description This property contains the columns for the documents table.
   * @memberof SsoEmployeePageComponent
   * @name SsoEmployeePageComponent#tableColumns
   * @property
   * @protected
   * @type {string[]}
   */
  protected tableColumns: string[] = [
    'name', 'modified', 'series', 'size', '_actions'
  ];

  /**
   * @description This property contains the years to display in the select for documents.
   * @memberof SsoEmployeePageComponent
   * @name SsoEmployeePageComponent#years
   * @property
   * @protected
   * @type {number[]}
   */
  protected years: number[] = [...Array(5).keys()].map((_: number, i: number) => new Date().getFullYear() - (i + 1));

  /**
   * @description This property contains any errors that occurred during the execution of the page component.
   * @memberof SsoEmployeePageComponent
   * @name SsoEmployeePageComponent#error
   * @property
   * @public
   * @type {ErrorModel?}
   */
  public error?: ErrorModel;

  /**
   * @constructor
   * @description This method instantiates our component with injected dependencies.
   * @memberof SsoEmployeePageComponent
   * @name SsoEmployeePageComponent#constructor
   * @param {EmployeeService} employeeService The injected instance of our employee service provider.
   * @param {MatDialog} modalService The injected instance of our modal service provider.
   * @param {ActivatedRoute} route The activated route for the component.
   * @public
   */
  public constructor(
    /**
     * @description This property contains the injected instance of our employee service provider.
     * @memberof SsoEmployeePageComponent
     * @name SsoEmployeePageComponent#employeeService
     * @private
     * @readonly
     * @type {EmployeeService}
     */
    private readonly employeeService: EmployeeService,
    /**
     * @description This property contains the injected instance of our modal service provider.
     * @memberof SsoEmployeePageComponent
     * @name SsoEmployeePageComponent#modalService
     * @private
     * @readonly
     * @type {MatDialog}
     */
    private readonly modalService: MatDialog,
    /**
     * @description This property contains the activated route for the component.
     * @memberof SsoEmployeePageComponent
     * @name SsoEmployeePageComponent#route
     * @private
     * @property
     * @readonly
     * @type {ActivatedRoute}
     */
    private readonly route: ActivatedRoute
  ) {
  }

  /**
   * @description This method downloads the document for the employee.
   * @memberof SsoEmployeePageComponent
   * @method
   * @name SsoEmployeePageComponent#downloadDocument
   * @param {EmployeeApiDocumentModel} taxDocument The document to download.
   * @protected
   * @returns {void}
   */
  protected downloadDocument(taxDocument: EmployeeApiDocumentModel): void {

    // Reset the loading flag.
    this.loading = true;

    // Load the document for the employee from the .NET Backend API.
    this.employeeService.loadDocument(taxDocument.objectId, taxDocument.name).subscribe({

      // Catch any errors that occurred during the request.
      error: (_: Error): void => {

        // Hide the documents table.
        this.hideDocuments = true;

        // Hide the year select for documents.
        this.hideYearSelect = true;

        // Reset the loading flag.
        this.loading = false;

        // Reset the error model so the view is updated.
        this.error =
          ErrorModel.fromError(new Error('We\'re unable to load your document at this time.  Please refresh your session and try again.'),
            HttpStatusCode.InternalServerError, 'Unable to load document.', 'error_outline');
      },
      next: (r: string): void => {

        // Create a new anchor element.
        const anchor: HTMLAnchorElement = document.createElement('a');

        // Set the download attribute for the anchor element.
        anchor.download = taxDocument.name;

        // Set the href attribute for the anchor element.
        anchor.href = r;

        // Click the anchor element to download the document.
        anchor.click();

        // Clean up the anchor element.
        anchor.remove();

        // Reset the loading flag.
        this.loading = false;
      }
    });
  }

  /**
   * @description This method loads the document for the employee.
   * @memberof SsoEmployeePageComponent
   * @method
   * @name SsoEmployeePageComponent#loadDocument
   * @param {EmployeeApiDocumentModel} taxDocument The document to load.
   * @protected
   * @returns {void}
   */
  protected loadDocument(taxDocument: EmployeeApiDocumentModel): void {

    // Reset the loading flag.
    this.loading = true;

    // Load the document for the employee from the .NET Backend API.
    this.employeeService.loadDocument(taxDocument.objectId, taxDocument.name).subscribe({

      // Catch any errors that occurred during the request.
      error: (_: Error): void => {

        // Hide the documents table.
        this.hideDocuments = true;

        // Hide the year select for documents.
        this.hideYearSelect = true;

        // Reset the loading flag.
        this.loading = false;

        // Reset the error model so the view is updated.
        this.error =
          ErrorModel.fromError(new Error('We\'re unable to load your document at this time.  Please refresh your session and try again.'),
            HttpStatusCode.InternalServerError, 'Unable to load document.', 'error_outline');
      },
      next: (r: string): void => {

        // Localize the material dialog reference.
        const dialog: MatDialogRef<DocumentDialogComponent> = this.modalService.open(DocumentDialogComponent, {

          // Set the height of the dialog.
          height: '75%',

          // Set the width of the dialog.
          width: '75%'
        });

        // Set the data URI for the dialog.
        dialog.componentInstance.dataUri = `${r}#scrollbar=0&view=Fit&zoom=150`;

        // Set the file name for the dialog.
        dialog.componentInstance.fileName = taxDocument.name;

        // Reset the loading flag.
        this.loading = false;
      }
    });
  }

  /**
   * @description This method loads the documents for the employee.
   * @memberof SsoEmployeePageComponent
   * @method
   * @name SsoEmployeePageComponent#loadDocuments
   * @param {number} taxYear The tax year to load the documents for.
   * @protected
   * @returns {void}
   */
  protected loadDocuments(taxYear: number): void {

    // Reset the loading flag.
    this.loading = true;

    // Load the documents for the employee from the .NET Backend API.
    this.employeeService.listDocuments(taxYear).subscribe({

      // Catch any errors that occurred during the request.
      error: (_: Error): void => {

        // Hide the documents table.
        this.hideDocuments = true;

        // Hide the year select for documents.
        this.hideYearSelect = true;

        // Reset the loading flag.
        this.loading = false;

        // Reset the error model so the view is updated.
        this.error =
          ErrorModel.fromError(new Error('We\'re unable to load your documents at this time.  Please refresh your session and try again.'),
            HttpStatusCode.InternalServerError, 'Unable to load documents.', 'error_outline');
      },

      // Handle the response from the .NET Backend API.
      next: (r: EmployeeApiDocumentsModel): void => {

        // Clear any existing error model.
        this.error = undefined;

        // Check that we have documents to display.
        if (r.a.length || r.b.length || r.c.length) {

          // Iterate over the a-series documents and update the series.
          if (r.a.length) r.a.forEach((d: EmployeeApiDocumentModel): string => d.series = 'A Series');

          // Iterate over the b-series documents and update the series.
          if (r.b.length) r.b.forEach((d: EmployeeApiDocumentModel): string => d.series = 'B Series');

          // Iterate over the c-series documents and update the series.
          if (r.c.length) r.c.forEach((d: EmployeeApiDocumentModel): string => d.series = 'C Series');

          // Update the documents for the view.
          this.documents = r.a.concat(r.b, r.c);

          // Display the documents table.
          this.hideDocuments = !(this.documents.length > 0);
        } else {

          // Reset the documents.
          this.documents = [];

          // Ensure the documents table remains hidden.
          this.hideDocuments = true;
        }

        // Reset the loading flag.
        this.loading = false;
      }
    });
  }

  /**
   * @description This method handles the change event for the year select.
   * @memberof SsoEmployeePageComponent
   * @method
   * @name SsoEmployeePageComponent#onYearSelectChange
   * @param {MatSelectChange} event The change event for the year select.
   * @protected
   * @returns {void}
   */
  public onYearSelectChange(event: MatSelectChange): void {

    // Reset the tax year to the selected year.
    this.currentYear = parseInt(event.value, 10);

    // We're done, load the documents and return.
    this.loadDocuments(this.currentYear);
  }

  /**
   * @description This method initializes the component.
   * @memberof SsoEmployeePageComponent
   * @method
   * @name SsoEmployeePageComponent#ngOnInit
   * @public
   * @returns {void}
   */
  public ngOnInit(): void {

    // Bind to the employee service events that we need to.
    this.employeeService.events.subscribe((e: EmployeeEvent): void => {

      // Check for a session-start event.
      if (e.type === EmployeeEventTypeEnum.SessionStart) {

        // Show the year select for documents.
        this.hideYearSelect = false;
      }

      // Check for a session-end event.
      if (e.type === EmployeeEventTypeEnum.SessionEnd) {

        // Hide the documents table.
        this.hideDocuments = true;

        // Hide the year select for documents.
        this.hideYearSelect = true;
      }

      // Check for a session-expired event.
      if (e.type === EmployeeEventTypeEnum.SessionExpired) {

        // Hide the documents table.
        this.hideDocuments = true;

        // Hide the year select for documents.
        this.hideYearSelect = true;

        // Reset the error model so the view is updated.
        this.error =
          ErrorModel.fromError(new Error('Your session has expired.  Please refresh your session and try again.'),
            HttpStatusCode.Unauthorized, 'Session Expired.', 'lock_open');
      }
    });

    // Subscribe to the query parameters of the activated route.
    this.route.queryParams.subscribe(async (params: Params): Promise<void | boolean> => {

      // Ensure we have an authentication key to validate, reset the error model if not.
      if (!params?.['key']) this.error = new ErrorModel('Missing PrismHR authentication key.',
        HttpStatusCode.BadRequest, [], 'Invalid PrismHR Employee SSO Session.');

      // Ensure we have a PEO ID to validate, reset the error model if not.
      else if (!params?.['peo_id'] && !params?.['peoId'] && !params?.['peoid'])
        this.error = new ErrorModel('Missing PrismHR PEO ID.', HttpStatusCode.BadRequest, [],
          'Invalid PrismHR Employee SSO Session.');

      // Otherwise, we have the necessary parameters so validate the incoming PrismHR SSO session with the .NET Backend API then subscribe to the result.
      else this.employeeService.signIn(params?.['peo_id'] ?? params?.['peoId'] ?? params?.['peoid'], params['key'])
          .subscribe({

            // Catch any errors that occurred during the request.
            error: (e: any): void => {

              // Reset the error model so the view is updated.
              this.error = ErrorModel.fromError((e instanceof Error) ? e :
                  new Error('An unknown error occurred while trying to validate the SSO token.'),
                HttpStatusCode.Unauthorized, 'Unable to Validate Employee SSO Session.', 'lock_person');
            }
          });
    });
  }
}
